import React, { useState } from 'react';
import { ArrowLeft, Library, Trash2, Copy, Eye, Loader2 } from 'lucide-react';
import { toast } from '../utils/toast';

/**
 * The facilitator's private archive of past sessions. Each archive can be
 * viewed read-only or cloned as a template (row skeleton + case stages,
 * all un-revealed) into a fresh session for the next class.
 */
export default function LibraryScreen({ archives, loading, onView, onClone, onDelete, onBack }) {
  const [cloning, setCloning] = useState(null); // archive id with the clone form open
  const [newId, setNewId] = useState('');
  const [busy, setBusy] = useState(false);

  const startClone = (archive) => {
    setCloning(archive.id);
    setNewId(`${(archive.sourceSessionId || 'case')}-${new Date().toISOString().slice(5, 10)}`);
  };

  const submitClone = async (archive) => {
    setBusy(true);
    try {
      const id = await onClone(archive, newId);
      toast(`Template ready, session “${id}” created.`, 'success');
      setCloning(null);
    } catch (e) {
      toast(e.message || 'Clone failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-page p-4 sm:p-8">
      <div className="max-w-3xl mx-auto">
        <div className="bg-surface border border-line rounded-card p-5 sm:p-8">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-heading sm:text-title font-normal text-ink flex items-center gap-2.5">
              <Library className="w-5 h-5 text-accent" />My case library
            </h1>
            <button onClick={onBack} className="flex items-center gap-1.5 text-body text-ink-2 hover:text-ink transition">
              <ArrowLeft className="w-4 h-4" />Back
            </button>
          </div>

          {loading && (
            <p className="flex items-center justify-center gap-2 text-body text-ink-2 py-10">
              <Loader2 className="w-4 h-4 animate-spin" />Loading…
            </p>
          )}

          {!loading && !archives.length && (
            <div className="text-center py-10 text-ink-2">
              <p className="text-body">No archived sessions yet.</p>
              <p className="text-meta mt-1 max-w-sm mx-auto">
                As a session facilitator, use <span className="font-medium">Facilitator → End &amp; archive</span> to
                save a session here. Archives are tied to this browser/device.
              </p>
            </div>
          )}

          <div className="space-y-3">
            {archives.map((archive) => (
              <div key={archive.id} className="border border-line rounded-card p-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <h2 className="text-body font-semibold text-ink">{archive.title}</h2>
                    <p className="text-meta text-ink-2 mt-0.5">
                      Session “{archive.sourceSessionId}” · archived {new Date(archive.archivedAt).toLocaleDateString()}
                      {archive.data?.case?.stages && ` · ${Object.keys(archive.data.case.stages).length} case stages`}
                    </p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => onView(archive)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-ctl border border-line text-meta text-ink hover:bg-accent-tint transition">
                      <Eye className="w-3.5 h-3.5" />View
                    </button>
                    <button onClick={() => startClone(archive)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-ctl bg-accent text-white text-meta hover:bg-accent-strong transition">
                      <Copy className="w-3.5 h-3.5" />Use as template
                    </button>
                    <button onClick={() => onDelete(archive)} className="p-1.5 rounded-ctl text-ink-2/50 hover:text-flag-red hover:bg-flag-red/5 transition" aria-label={`Delete archive "${archive.title}"`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {cloning === archive.id && (
                  <div className="mt-3 pt-3 border-t border-line flex flex-col sm:flex-row gap-2">
                    <input
                      value={newId}
                      onChange={(e) => setNewId(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && !busy && submitClone(archive)}
                      placeholder="New session ID"
                      autoFocus
                      className="flex-1 px-3 py-2 border border-line rounded-ctl text-body outline-none focus:ring-2 focus:border-accent focus:ring-accent/25"
                    />
                    <div className="flex gap-2">
                      <button onClick={() => setCloning(null)} className="px-3 py-2 text-meta text-ink-2 hover:bg-accent-tint rounded-ctl transition">Cancel</button>
                      <button
                        onClick={() => submitClone(archive)}
                        disabled={busy || !newId.trim()}
                        className="px-4 py-2 bg-accent text-white text-meta font-medium rounded-ctl hover:bg-accent-strong transition disabled:opacity-50"
                      >
                        {busy ? 'Creating…' : 'Create session'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
